// aluno/login.js — tela de login via Google OAuth (GIS).
//
// Substitui a versão anterior com teclado CPF (preservada no histórico git).
// Contrato de export mantido: export function render(container).
//
// Fluxo:
//   1. Boot: tenta restaurar sessão (auth.restoreSession). Se válida, redireciona.
//   2. Carrega lista de alunos da API → auth.setAlunos (lookup email→aluno)
//   3. Inicializa GIS + callback onCredential
//   4. Renderiza botão "Entrar com Google"
//   5. Callback: auth.signInWithGoogleToken(idToken)
//      - ok: router.go('/admin' ou '/aluno') conforme role
//      - EMAIL_NAO_CADASTRADO: mostra fallback com botão WhatsApp para secretaria
//      - outros erros: notify.error
//
// Whitelist admin temporária (pré-commit 6):
//   Antes de o backend /sessao existir, o frontend reconhece os 8 e-mails admin
//   através de uma constante local. Isso é INTERMEDIÁRIO — commit 6 move para
//   Script Properties. Mantém admin master operacional durante a migração.

import { api }    from '../core/api.js';
import { auth }   from '../core/auth.js';
import { notify } from '../core/notify.js';
import { router } from '../core/router.js';
import { initGoogleAuth, renderGoogleButton } from '../core/google-auth.js';
import { openConsentModal, loadConsentSnapshot } from './consent-modal.js';
import { trackLogin, trackLoginFailed } from '../core/analytics.js';

// ── Whitelist admin intermediária (removida em C6 quando backend assumir) ───

const ADMIN_WHITELIST_LOCAL = Object.freeze([
  'yangmendes@gmail.com',
  'gblprado@outlook.com',
  'gbllprado@icloud.com',
  'gbllprado@gmail.com',
  'eloquenceoratoria@gmail.com',
  'cytreinamentos@gmail.com',
  'gabriela.eloquence@gmail.com',
  'lucascerqueirapro@gmail.com',
]);

function isAdminEmail(email) {
  if (typeof email !== 'string') return false;
  return ADMIN_WHITELIST_LOCAL.includes(email.trim().toLowerCase());
}

// ── Número WhatsApp da secretaria (mensagem de fallback) ────────────────────

const WHATSAPP_SECRETARIA = '5571982505900'; // Eloquence Escola de Oratória (oficial)

function waLinkSecretaria(emailGoogle, nomeGoogle) {
  const msg = `Olá! Tentei entrar no App Eloquence com meu Google (${emailGoogle}), mas o sistema diz que meu e-mail não está cadastrado. Poderiam atualizar?\n\nNome: ${nomeGoogle || '(não informado)'}`;
  return `https://wa.me/${WHATSAPP_SECRETARIA}?text=${encodeURIComponent(msg)}`;
}

// ── Estado do módulo ────────────────────────────────────────────────────────

let _alunosCarregados = false;
let _containerRef = null;

// ── Carregamento da lista de alunos ─────────────────────────────────────────

async function _carregarAlunos() {
  if (_alunosCarregados) return true;
  try {
    const data = await api.get('alunos');
    if (!Array.isArray(data)) {
      console.error('[login] api.get(alunos) retornou shape inesperado:', data);
      notify.error('Não foi possível carregar a lista de alunos.');
      return false;
    }
    auth.setAlunos(data);
    _alunosCarregados = true;
    return true;
  } catch (e) {
    console.error('[login] erro ao carregar alunos:', e);
    notify.error('Falha de conexão. Verifique sua internet.');
    return false;
  }
}

// ── Callback do GIS ─────────────────────────────────────────────────────────

async function _onGoogleCredential(idToken) {
  if (!_alunosCarregados) {
    const ok = await _carregarAlunos();
    if (!ok) return;
  }

  const result = await auth.signInWithGoogleToken(idToken);

  if (result.ok) {
    // Promove a admin se o e-mail estiver na whitelist local (intermediário, C6 substitui)
    if (isAdminEmail(result.user.email)) {
      auth.patchCurrent({ role: 'admin', turma: 'Admin' });
    }

    // Gate LGPD: se ainda não aceitou o termo atual, abre o modal bloqueante.
    // Snapshot fica em sessionStorage (H4 moverá para backend).
    const ok = await _ensureConsent(result.user.nome);
    if (!ok) {
      // Recusou ou fechou: aborta login e volta à tela inicial.
      await auth.logout();
      notify.error('Para usar o App Eloquence é necessário aceitar o termo.');
      trackLoginFailed('consent_refused');
      return;
    }

    // Analytics: login bem-sucedido. Enviamos apenas a flag is_admin,
    // nunca email ou nome — PII fica fora do GA4.
    trackLogin({ is_admin: auth.isAdmin(), method: 'google' });

    const destino = auth.isAdmin() ? '/admin' : '/aluno';
    router.go(destino);
    return;
  }

  if (result.code === 'EMAIL_NAO_CADASTRADO') {
    // Admin que é admin só pela whitelist (não está na FICHA LIMPA) → libera
    if (isAdminEmail(result.email)) {
      // Cria sessão admin manualmente (caso extremo de admin não-aluno)
      const r = await auth.signInWithGoogleToken(idToken); // retentativa registra falha
      void r;
      // Como signIn já retornou EMAIL_NAO_CADASTRADO, precisamos injetar o usuário:
      auth.setAlunos([...auth.getAlunos(), {
        nome: result.nome || result.email,
        email: result.email,
        cpf: '',
        turma: 'Admin',
      }]);
      const retry = await auth.signInWithGoogleToken(idToken);
      if (retry.ok) {
        auth.patchCurrent({ role: 'admin', turma: 'Admin' });
        // Admin também precisa aceitar LGPD
        const ok = await _ensureConsent(retry.user.nome);
        if (!ok) {
          await auth.logout();
          notify.error('Para usar o App Eloquence é necessário aceitar o termo.');
          trackLoginFailed('consent_refused');
          return;
        }
        trackLogin({ is_admin: true, method: 'google' });
        router.go('/admin');
        return;
      }
    }
    trackLoginFailed('email_nao_cadastrado');
    _renderFallback(result.email, result.nome);
    return;
  }

  if (result.code === 'EMAIL_NAO_VERIFICADO') {
    notify.error('Seu e-mail do Google não está verificado.');
    trackLoginFailed('email_nao_verificado');
    return;
  }

  trackLoginFailed(result.code || 'auth_error');
  notify.error('Não foi possível autenticar. Tente novamente.');
}

// ── Gate LGPD (modal de consentimento) ────────────────────────────

/**
 * Garante que o usuário aceitou a versão atual do termo.
 * - Se já há snapshot válido (mesma versão), retorna true imediatamente.
 * - Senão, abre o modal bloqueante.
 * - Retorna true se aceitou; false se recusou/cancelou.
 */
async function _ensureConsent(nome) {
  const snapshot = loadConsentSnapshot();
  if (snapshot?.accepted) return true;
  const primeiro = (nome || '').split(' ')[0];
  const result = await openConsentModal({ nomeUsuario: primeiro });
  return !!(result && result.accepted);
}

// ── Fallback: e-mail não cadastrado ─────────────────────────────────────────

function _renderFallback(email, nomeGoogle) {
  if (!_containerRef) return;
  const wa = waLinkSecretaria(email, nomeGoogle);
  _containerRef.innerHTML = `
    <div class="login-page" id="login-root">
      <div class="login-bg"></div>
      <div class="login-card login-card-fallback">
        <div class="login-logo-wrap">
          <img class="login-logo-img" src="/wing.png" alt="Eloquence" decoding="async" />
        </div>
        <h1 class="login-brand">Eloquence</h1>
        <p class="login-tagline">Escola de Oratória</p>
        <h2 class="login-title" style="margin-top:18px">E-mail não cadastrado</h2>
        <p class="login-subtitle">
          O e-mail <strong>${_escapeHtml(email)}</strong> não está<br>
          vinculado a nenhum aluno da escola.
        </p>
        <p class="login-subtitle" style="margin-top:12px">
          Entre em contato com a secretaria para atualizar seu cadastro.
        </p>
        <a class="login-fallback-btn" href="${wa}" target="_blank" rel="noopener">
          <span>💬</span>
          <span>Falar com a secretaria</span>
        </a>
        <button class="login-fallback-back" id="login-fallback-back" type="button">
          ← Tentar com outra conta
        </button>
      </div>
      <div class="login-version" aria-hidden="true">v19.0.0</div>
    </div>
  `;
  _containerRef.querySelector('#login-fallback-back')?.addEventListener('click', () => {
    auth.clearEmailDivergente();
    render(_containerRef);
  });
}

function _escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

// ── Render principal ────────────────────────────────────────────────────────

export async function render(container) {
  if (!container) {
    console.error('[login] render chamado sem container');
    return;
  }
  _containerRef = container;

  // Fast-path: sessão válida? pula tela de login
  const restored = auth.restoreSession();
  if (restored) {
    router.go(auth.isAdmin() ? '/admin' : '/aluno');
    return;
  }

  // Monta o DOM inicial
  container.innerHTML = `
    <div class="login-page" id="login-root">
      <div class="login-bg"></div>
      <div class="login-card">
        <div class="login-logo-wrap">
          <img class="login-logo-img" src="/wing.png" alt="Eloquence" decoding="async" />
        </div>
        <h1 class="login-brand">Eloquence</h1>
        <p class="login-tagline">Escola de Oratória</p>

        <h2 class="login-title">Bem-vindo</h2>
        <p class="login-subtitle">Entre com sua conta Google<br>para acessar o app</p>

        <div class="login-google-wrap" id="login-google-wrap">
          <div class="login-google-loading">Carregando…</div>
        </div>

        <p class="login-hint">
          Alunos: use o mesmo e-mail cadastrado na ficha da escola.
        </p>
      </div>
      <div class="login-version" aria-hidden="true">v19.0.0</div>
    </div>
  `;

  // Carrega alunos em paralelo com GIS
  const alunosPromise = _carregarAlunos();

  try {
    await initGoogleAuth(_onGoogleCredential);
    const wrap = container.querySelector('#login-google-wrap');
    if (wrap) {
      await renderGoogleButton(wrap, { theme: 'filled_blue', size: 'large', width: 280 });
    }
  } catch (e) {
    console.error('[login] GIS falhou:', e);
    const wrap = container.querySelector('#login-google-wrap');
    if (wrap) {
      wrap.innerHTML = `<div class="login-google-error">
        Falha ao carregar Google Sign-In. Verifique sua conexão e recarregue a página.
      </div>`;
    }
  }

  await alunosPromise; // garante que callback tem a lista quando chamado
}
